import type { FeatureFlags } from '@/shared/core/types/feature-flags.ts'

// TODO PostHog: Uncomment when migrating to PostHog
// import posthog from 'posthog-js'
// import { usePostHog } from 'posthog-js/react'

/**
 * Generates dot-notation paths for all boolean properties in a nested object type.
 * Example: { a: { b: boolean } } => 'a.b'
 */
type BooleanPaths<T, Prefix extends string = ''> = {
	[K in keyof T]: T[K] extends boolean
		? Prefix extends ''
			? K & string
			: `${Prefix}.${K & string}`
		: T[K] extends object
			? BooleanPaths<
					T[K],
					Prefix extends '' ? K & string : `${Prefix}.${K & string}`
				>
			: never
}[keyof T]

export type FeatureFlagKey = BooleanPaths<FeatureFlags>

/**
 * Gets a nested value from an object using a dot-notation path.
 */
function getNestedValue(obj: Record<string, unknown>, path: string): boolean {
	const keys = path.split('.')
	let current: unknown = obj

	for (const key of keys) {
		if (current === null || current === undefined) {
			return false
		}
		current = (current as Record<string, unknown>)[key]
	}

	return Boolean(current)
}

/**
 * Checks if a specific feature flag is enabled.
 * Can be used outside of React components (e.g., in router loaders).
 *
 * @example
 * if (isFeatureFlagEnabled('adminConsole.createClient')) { ... }
 */
export function isFeatureFlagEnabled(key: FeatureFlagKey): boolean {
	// Current implementation : uses window.featureFlags
	return getNestedValue(
		window.featureFlags as unknown as Record<string, unknown>,
		key,
	)

	// TODO PostHog: Replace with PostHog's non-hook API
	// return posthog.isFeatureEnabled(key) ?? false
}

/**
 * Hook to check if a specific feature flag is enabled.
 * Uses a type-safe dot-notation key to access nested feature flags.
 *
 * @example
 * const canCreateClient = useFeatureFlag('adminConsole.createClient')
 * const canEditClient = useFeatureFlag('adminConsole.client.edit')
 */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
	// Current implementation : delegates to isFeatureFlagEnabled
	return isFeatureFlagEnabled(key)

	// TODO PostHog: Replace with PostHog's React hook for automatic re-renders on flag changes
	// const posthog = usePostHog()
	// return posthog.isFeatureEnabled(key) ?? false
}
